import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

type Appointment = {
  _id: string;
  title: string;
  startAt: string;
  endAt: string;
  status: string;
  hostUserId: string;
  contactId?: string | null;
};

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'bg-dgal-navy-2 border-dgrp-red/40',
  confirmed: 'bg-dgrp-red/30 border-dgrp-red',
  completed: 'bg-dgal-mist/20 border-dgal-mist',
  no_show: 'bg-red-700/30 border-red-700',
  cancelled: 'bg-dgal-ink border-dgal-mist/40',
  rescheduled: 'bg-dgal-navy/60 border-dgrp-red/30',
};

function startOfWeek(d: Date) {
  const day = d.getDay();
  const sun = new Date(d);
  sun.setDate(d.getDate() - day);
  sun.setHours(0, 0, 0, 0);
  return sun;
}

export default function Calendar() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [appts, setAppts] = useState<Appointment[]>([]);
  const [error, setError] = useState<string | null>(null);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      return d;
    }),
    [weekStart]
  );

  async function load() {
    try {
      const startAfter = weekStart.toISOString();
      const end = new Date(weekStart);
      end.setDate(end.getDate() + 7);
      const { data } = await api.get(`/api/appointments?startAfter=${startAfter}&startBefore=${end.toISOString()}`);
      setAppts(data.appointments || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load calendar');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart]);

  function shift(deltaDays: number) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaDays);
    setWeekStart(startOfWeek(d));
  }

  return (
    <main className="text-white">
      <section className="max-w-7xl mx-auto px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="h-display text-3xl">Calendar</h1>
          <Link to="/calendar/settings" className="text-dg-text-2 hover:text-dgrp-red text-sm">
            Settings
          </Link>
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-2">
            <button onClick={() => shift(-7)} className="px-3 py-1 border border-dgal-navy-2 rounded">
              ← Prev
            </button>
            <button onClick={() => setWeekStart(startOfWeek(new Date()))} className="px-3 py-1 border border-dgal-navy-2 rounded">
              Today
            </button>
            <button onClick={() => shift(7)} className="px-3 py-1 border border-dgal-navy-2 rounded">
              Next →
            </button>
          </div>
          <div className="text-dgal-mist text-sm">{weekStart.toDateString()}</div>
        </div>

        {error && <div className="text-red-400 text-sm mb-4">{error}</div>}

        <div className="grid grid-cols-7 gap-2">
          {days.map((d) => {
            const dayAppts = appts.filter((a) => {
              const s = new Date(a.startAt);
              return s.toDateString() === d.toDateString();
            });
            return (
              <div key={d.toISOString()} className="min-h-[200px] border border-dgal-navy-2 rounded p-2 bg-dgal-navy/30">
                <div className="text-xs uppercase tracking-wider text-dgal-mist mb-2">
                  {d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' })}
                </div>
                <ul className="space-y-1">
                  {dayAppts.map((a) => (
                    <li
                      key={a._id}
                      className={`text-xs rounded px-2 py-1 border ${STATUS_COLORS[a.status] || 'border-dgal-navy-2'}`}
                    >
                      <div className="font-medium truncate">{a.title}</div>
                      <div className="text-dgal-mist">
                        {new Date(a.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>
    </main>
  );
}
